// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { xIsAuthenticated } from "@rpsc/App/Store/Selectors/GeneralSelectors";
import Link, { LinkPresentationProps } from "@rpsc/MobiusOverrides/Link/Link";
import TextField, { TextFieldProps } from "@rpsc/MobiusOverrides/TextField";
import CartLineNotes, { CartLineNotesStyles } from "@rpsc/Overrides/Widgets/Cart/CartLineNotes";
import IconPencilEdit from "@rpsc/XDL/Icons/IconPencilEdit";
import IconPlus from "@rpsc/XDL/Icons/IconPlus";
import { mergeWithCss } from "@rpsc/XDL/Utilities/mergeWithCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    showLineNotes?: boolean;
    extendedStyles?: CustomerLineNumberAndNotesStyles;
    editableNote?: boolean;
    cart: Cart;
}
const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    updateCartLine,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CustomerLineNumberAndNotesStyles {
    wrapper?: InjectableCss;
    lineNotesModalStyles?: ModalPresentationProps;
    modalWrapper?: InjectableCss;
    customerLineInput?: TextFieldProps;
    saveButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    lineNotesLinkAdd?: LinkPresentationProps;
    lineNotesLinkHide?: LinkPresentationProps;
    infoWrapper?: InjectableCss;
    customerLineNumberWrapper?: InjectableCss;
    customerLineNumberHeading?: TypographyPresentationProps;
    customerLineNumberText?: TypographyPresentationProps;
    buttonsWrapper?: InjectableCss;
    cartLineNotes?: CartLineNotesStyles;
}

export const customerLineNumberAndNotesStyles: CustomerLineNumberAndNotesStyles = {};

const CustomerLineNumberAndNotes = ({
    showLineNotes,
    extendedStyles,
    editableNote,
    cart,
    cartLine,
    updateCartLine,
    isAuthenticated,
}: Props) => {
    const [styles] = useState(() => mergeToNew(customerLineNumberAndNotesStyles, extendedStyles));
    const [lineNotesModalOpen, setLineNotesModalOpen] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const [customerLineNumber, setCustomerLineNumber] = useState(cartLine.properties?.customerLineNumber || "");
    const [notes, setNotes] = useState(cartLine?.notes);
    const [isDataChanged, setIsDataChanged] = useState(false);

    useEffect(() => {
        setCustomerLineNumber(cartLine.properties?.customerLineNumber);
    }, [cartLine.properties?.customerLineNumber]);

    const handleLineNotesModalClose = () => {
        setLineNotesModalOpen(false);
        setIsDataChanged(false);
        setCustomerLineNumber(cartLine.properties?.customerLineNumber || "");
    };

    const handleLineNotesModalOpen = () => {
        setLineNotesModalOpen(true);
    };

    const handleLineNotesSubmit = () => {
        if (isDataChanged) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                    properties: {
                        customerLineNumber,
                    },
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Note/ Customer Line # Updated"),
                        messageType: "success",
                    });
                    handleLineNotesModalClose();
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        setNotes(notes);
        setIsDataChanged(notes !== cartLine.notes);
    };

    const customerLineNumberChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomerLineNumber(event.currentTarget.value);
        setIsDataChanged(event.currentTarget.value !== cartLine.properties.customerLineNumber);
    };

    const heading = isAuthenticated
        ? cartLine.notes || cartLine.properties.customerLineNumber
            ? "Edit Line Note/ Customer Line #"
            : "Add Line Note/ Customer Line #"
        : cartLine.notes
        ? "Edit Line Note"
        : "Add Line Note";

    return (
        <StyledWrapper {...styles.wrapper}>
            {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && editableNote && (
                <Link
                    onClick={handleLineNotesModalOpen}
                    data-test-selector="cartline_notesShowHideButton"
                    aria-label={heading}
                    {...(!cartLine.notes && !cartLine.properties.customerLineNumber
                        ? styles.lineNotesLinkHide
                        : styles.lineNotesLinkAdd)}
                >
                    {translate(heading)}
                    <VisuallyHidden>{translate(heading)}</VisuallyHidden>
                </Link>
            )}
            <StyledWrapper {...styles.infoWrapper}>
                {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                    <CartLineNotes cart={cart} editable={false} showLabel={true} />
                )}

                {cartLine.properties.customerLineNumber && isAuthenticated && (
                    <StyledWrapper {...styles.customerLineNumberWrapper}>
                        <Typography {...styles.customerLineNumberHeading}>{translate("Customer Line #")}</Typography>
                        <Typography {...styles.customerLineNumberText}>
                            {cartLine.properties.customerLineNumber}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <Modal
                {...styles.lineNotesModalStyles}
                isOpen={lineNotesModalOpen}
                closeOnEsc={true}
                handleClose={handleLineNotesModalClose}
                headline={translate(heading)}
            >
                <StyledWrapper {...styles.modalWrapper}>
                    {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                        <CartLineNotes
                            cart={cart}
                            editable={editableNote}
                            onNotesChange={notesChangeHandler}
                            showLabel={true}
                            returnNotesOnChange={true}
                            extendedStyles={styles.cartLineNotes}
                            label={translate(cartLine.notes ? "Line Note" : "Add Line Note")}
                            isOptional={true}
                        />
                    )}

                    {isAuthenticated && (
                        <TextField
                            {...styles.customerLineInput}
                            label={translate(
                                cartLine.properties.customerLineNumber ? "Customer Line #" : "Add Customer Line #",
                            )}
                            value={customerLineNumber}
                            data-test-selector="customer_line_number"
                            placeholder={translate("Enter Name As It Shows on Card")}
                            onChange={customerLineNumberChangeHandler}
                            isOptional={true}
                        />
                    )}
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button {...styles.cancelButton} onClick={handleLineNotesModalClose}>
                            {translate("Cancel")}
                        </Button>
                        <Button
                            disabled={!isDataChanged}
                            {...styles.saveButton}
                            onClick={handleLineNotesSubmit}
                            aria-label={translate("Save")}
                        >
                            {translate("Save")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CustomerLineNumberAndNotes));
